import { Show, createEffect, onCleanup, onMount, type Component } from 'solid-js';
import uPlot from 'uplot';
import 'uplot/dist/uPlot.min.css';
import type { LedgerRow, SessionTrace } from '~/domain/layered';
import { formatCents } from '~/domain/profiles';
import { SESSION_END_LABELS } from '~/view/labels';

const PX_PER_SPIN = 6;

export interface TrajectoryChartProps {
  trace: SessionTrace;
  /** Buy-in in cents — the reference line that says whether you are ahead. */
  buyInCents: number;
  currency: string;
  /** Named in the caption, so the sample is always attributed. */
  strategyName: string;
  class?: string;
}

/** The three bands of a bar, plus the tallest total, for the y scale. */
interface Segments {
  readonly atRisk: readonly number[];
  readonly inRack: readonly number[];
  readonly pocketed: readonly number[];
  readonly peakTotalCents: number;
}

/**
 * Everything the canvas hooks read. uPlot's draw hooks are plain closures
 * created once, so anything that changes between traces has to live in a
 * mutable object they hold a reference to rather than be captured by value.
 */
interface ChartState {
  currency: string;
  buyInCents: number;
  yMaxCents: number;
  atRisk: readonly number[];
  inRack: readonly number[];
  pocketed: readonly number[];
  gateSpin: number | null;
  gateLabel: string;
}

function cssVar(name: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback;
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value === '' ? fallback : value;
}

function extractSegments(rows: readonly LedgerRow[]): Segments {
  const atRisk: number[] = [];
  const inRack: number[] = [];
  const pocketed: number[] = [];
  let peak = 0;
  for (const row of rows) {
    atRisk.push(row.atRiskCents);
    inRack.push(row.inRackCents);
    pocketed.push(row.pocketedCents);
    peak = Math.max(peak, row.atRiskCents + row.inRackCents + row.pocketedCents);
  }
  return { atRisk, inRack, pocketed, peakTotalCents: peak };
}

/**
 * Cumulative-from-the-top data for uPlot's stacked-bar draw order. See the
 * component doc comment for why the first series must be the grand total.
 */
function buildData(rows: readonly LedgerRow[], segments: Segments): uPlot.AlignedData {
  const xs = rows.map((row) => row.spin);
  const n = rows.length;
  const workingAndPocketed = new Array<number>(n);
  const total = new Array<number>(n);
  for (let i = 0; i < n; i += 1) {
    const atRisk = segments.atRisk[i] ?? 0;
    const inRack = segments.inRack[i] ?? 0;
    const pocketed = segments.pocketed[i] ?? 0;
    const workingAndPocketedValue = inRack + pocketed;
    workingAndPocketed[i] = workingAndPocketedValue;
    total[i] = atRisk + workingAndPocketedValue;
  }
  return [xs, total, workingAndPocketed, [...segments.pocketed]];
}

function drawBuyInLine(state: ChartState, color: string) {
  return (u: uPlot) => {
    const y = u.valToPos(state.buyInCents, 'y', true);
    if (!Number.isFinite(y)) return;
    const { ctx } = u;
    ctx.save();
    ctx.strokeStyle = color;
    ctx.setLineDash([4, 4]);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(u.bbox.left, y);
    ctx.lineTo(u.bbox.left + u.bbox.width, y);
    ctx.stroke();
    ctx.restore();
  };
}

/** Vertical rule at the last spin plus a small label naming which gate fired. */
function drawGateMarker(state: ChartState, color: string) {
  return (u: uPlot) => {
    if (state.gateSpin == null) return;
    const x = u.valToPos(state.gateSpin, 'x', true);
    if (!Number.isFinite(x)) return;
    const { ctx } = u;
    ctx.save();
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(x, u.bbox.top);
    ctx.lineTo(x, u.bbox.top + u.bbox.height);
    ctx.stroke();

    // The gate always sits at the last spin, which by default is the run's
    // right edge, so the label has to be willing to fall back to the left of
    // its own line or it would be clipped off the plot on every session.
    ctx.font = '600 10px sans-serif';
    ctx.textBaseline = 'top';
    const label = state.gateLabel;
    const textWidth = ctx.measureText(label).width;
    const fitsRight = x + 4 + textWidth <= u.bbox.left + u.bbox.width;
    const textX = fitsRight ? x + 4 : x - 4 - textWidth;
    ctx.fillStyle = color;
    ctx.fillText(label, textX, u.bbox.top + 2);
    ctx.restore();
  };
}

function buildOptions(width: number, height: number, state: ChartState): uPlot.Options {
  const inkFaint = cssVar('--color-ink-faint', '#63637a');
  const lineColor = cssVar('--color-line', '#24242f');
  // At-risk and in-rack share the "placed coverage" violet family so the two
  // read as one working-pot mass at a glance; pocketed is a wholly different
  // hue (the app's gain green) because it is a different pot — banked, safe,
  // unreachable — not a shade of the same money.
  const atRiskColor = cssVar('--color-placed-strong', '#a78bfa');
  const inRackColor = cssVar('--color-placed-deep', '#6d28d9');
  const pocketedColor = cssVar('--color-gain', '#34d399');
  const gateColor = cssVar('--color-remove', '#f59e0b');

  const money = (raw: number | null | undefined) =>
    raw === null || raw === undefined ? '--' : formatCents(Math.round(raw), state.currency);

  // Plotted values are cumulative for the stacking arithmetic; the legend
  // and cursor readout should still say what that spin's actual segment was,
  // so these read the raw per-spin arrays on `state` by dataIdx rather than
  // formatting the cumulative number the series was drawn from.
  const segmentValue =
    (key: 'atRisk' | 'inRack' | 'pocketed') =>
    (_u: uPlot, _raw: number | null, _seriesIdx: number, idx: number | null) =>
      idx == null ? '--' : formatCents(state[key][idx] ?? 0, state.currency);

  const barsFactory = uPlot.paths.bars;
  const barPaths = barsFactory ? barsFactory({ size: [0.9] }) : undefined;

  return {
    width,
    height,
    scales: {
      x: { time: false },
      // A static function rather than uPlot's own data-derived auto-range:
      // the ceiling is maintained on `state` (grow-only across a trace swap)
      // so a toggle to a smaller-peak strategy cannot visibly rescale the
      // chart the reader is trying to compare against.
      y: { range: (_u, _min, _max) => [0, state.yMaxCents] },
    },
    series: [
      {},
      {
        label: 'At risk',
        stroke: 'transparent',
        fill: atRiskColor,
        paths: barPaths,
        points: { show: false },
        value: segmentValue('atRisk'),
      },
      {
        label: 'In rack',
        stroke: 'transparent',
        fill: inRackColor,
        paths: barPaths,
        points: { show: false },
        value: segmentValue('inRack'),
      },
      {
        label: 'Pocketed',
        stroke: 'transparent',
        fill: pocketedColor,
        paths: barPaths,
        points: { show: false },
        value: segmentValue('pocketed'),
      },
    ],
    axes: [
      {
        stroke: inkFaint,
        grid: { stroke: lineColor, width: 1 },
        ticks: { stroke: lineColor, width: 1 },
        label: 'Spin',
        labelSize: 16,
      },
      {
        stroke: inkFaint,
        grid: { stroke: lineColor, width: 1 },
        ticks: { stroke: lineColor, width: 1 },
        values: (_u, splits) => splits.map((v) => (v == null ? null : money(v))),
      },
    ],
    cursor: { points: { show: false } },
    legend: { show: true },
    hooks: {
      draw: [drawBuyInLine(state, inkFaint), drawGateMarker(state, gateColor)],
    },
  } satisfies uPlot.Options;
}

const TrajectoryChart: Component<TrajectoryChartProps> = (props) => {
  let scrollEl: HTMLDivElement | undefined;
  let containerEl: HTMLDivElement | undefined;
  let chart: uPlot | undefined;

  const state: ChartState = {
    currency: props.currency,
    buyInCents: props.buyInCents,
    yMaxCents: Math.max(props.buyInCents, 1),
    gateSpin: null,
    gateLabel: '',
    atRisk: [],
    inRack: [],
    pocketed: [],
  };

  const destroy = () => {
    chart?.destroy();
    chart = undefined;
  };

  /**
   * uPlot sizes only its canvas; the legend it renders underneath is extra
   * height on top of that. Handing it the wrapper's full height therefore
   * makes the whole chart taller than its wrapper by the legend's height,
   * and it spills over whatever follows. Reserve that space instead.
   */
  const plotHeight = (available: number): number => {
    const legend = chart?.root.querySelector<HTMLElement>('.u-legend');
    return Math.max(40, Math.floor(available) - (legend?.offsetHeight ?? 0));
  };

  /** ~6px per spin, floored at the wrapper's own width per §7.2. */
  const plotWidth = (available: number, spinCount: number): number =>
    Math.max(Math.floor(available), spinCount * PX_PER_SPIN);

  onMount(() => {
    if (!scrollEl) return;
    const el = scrollEl;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry || !chart || !containerEl) return;
      const width = plotWidth(entry.contentRect.width, props.trace.rows.length);
      containerEl.style.width = `${width}px`;
      chart.setSize({ width, height: plotHeight(entry.contentRect.height) });
    });
    ro.observe(el);
    onCleanup(() => {
      ro.disconnect();
      destroy();
    });
  });

  createEffect(() => {
    const trace = props.trace;
    state.currency = props.currency;
    state.buyInCents = props.buyInCents;

    const rows = trace.rows;
    if (!scrollEl || !containerEl || rows.length === 0) {
      destroy();
      return;
    }

    const segments = extractSegments(rows);
    state.atRisk = segments.atRisk;
    state.inRack = segments.inRack;
    state.pocketed = segments.pocketed;

    const lastRow = rows[rows.length - 1];
    state.gateSpin = lastRow ? lastRow.spin : null;
    state.gateLabel = SESSION_END_LABELS[trace.result.end];

    const candidateMaxCents = Math.max(segments.peakTotalCents, props.buyInCents) * 1.1;
    state.yMaxCents = chart
      ? Math.max(state.yMaxCents, candidateMaxCents)
      : Math.max(candidateMaxCents, 1);

    const data = buildData(rows, segments);
    const available = scrollEl.clientWidth || 320;
    const width = plotWidth(available, rows.length);
    containerEl.style.width = `${width}px`;

    if (!chart) {
      const height = Math.max(1, scrollEl.clientHeight || 200);
      chart = new uPlot(buildOptions(width, height, state), data, containerEl);
      // The legend only exists once the chart is built, so correct the
      // height now that it can actually be measured.
      chart.setSize({ width, height: plotHeight(scrollEl.clientHeight || height) });
      // Default view is the tail of the run, where the gate fired (§7.2).
      scrollEl.scrollLeft = scrollEl.scrollWidth;
      return;
    }

    chart.setData(data, false);
    chart.setScale('y', { min: 0, max: state.yMaxCents });
    chart.setSize({ width, height: plotHeight(scrollEl.clientHeight) });
  });

  return (
    <section class={`tnum rounded-lg border border-line bg-surface-1 p-3 ${props.class ?? ''}`}>
      <h2 class="mb-2 text-[10px] font-medium uppercase tracking-[0.12em] text-ink-faint">
        Trajectory
      </h2>
      <div ref={scrollEl} class="relative h-52 w-full overflow-x-auto">
        <div ref={containerEl} class="h-full" />
        <Show when={props.trace.rows.length === 0}>
          <p class="absolute inset-0 flex items-center justify-center text-center text-[11px] text-ink-faint">
            No simulation data yet.
          </p>
        </Show>
      </div>

      {/* Stated every time this renders, not just in the doc comment — the
          caption a reader can see is the one that does any work (§6.2). */}
      <p class="mt-2 text-[10px] leading-relaxed text-ink-faint">
        One session, drawn from the distribution on seed{' '}
        <span class="tnum text-ink-dim">{props.trace.seed}</span> — a labelled sample, not the
        result. Strategy: <span class="text-ink-dim">{props.strategyName}</span>.
      </p>
    </section>
  );
};

export default TrajectoryChart;
